/* ═══════════════════════════════════════════════════════════════
   WidgetGrid — free-form dashboard grid: free width, auto height.

   Built on react-grid-layout (battle-tested lib over hand-rolls,
   DESIGN.md §1). Every widget sits on a 12-column grid whose row unit
   is 0.5rem and whose gutter is 1rem — both re-derived from the LIVE
   root font size, so the grid follows the interface-scale dial.

   Height is never chosen by anyone: each card measures its own
   rendered content (ResizeObserver on an intrinsic-height inner
   wrapper) and its row span is derived from that. The card never
   scrolls — the host page is the one scroll container (§3.1). Height
   is derived, never persisted.

   Width is free: in Customize the right edge drags (or Left/Right
   arrows on the focused grip) between the widget's min/max columns.
   The rendered tier (compact / standard / expanded) is a breakpoint of
   the width — see tierFor.

   Below DASHBOARD_SINGLE_COLUMN_REM of container width the grid
   collapses to one column in reading order; the stored layout is left
   untouched and drag/resize pause until there is room again.

   Customize mode: drag-by-header, resize-by-east-edge, hide ✕,
   "+ Add widget" ghost card → catalog drawer, reset-to-default.
   ═══════════════════════════════════════════════════════════════ */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GridLayout, {
  bottom,
  useContainerWidth,
  verticalCompactor,
  type Layout,
  type LayoutItem,
  type ResizeHandleAxis,
} from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import './WidgetGrid.css';
import { MoreHorizontal } from 'lucide-react';
import { hasPermission, hasAnyPermission } from '../../shared/permissions';
import { DASHBOARD_SINGLE_COLUMN_REM, ROOT_PX } from '../../shared/uiScale';
import { rem } from '../lib/rem';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Icon } from './ui/Icon';
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
} from './ui/context-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { SIZE_COLS, tierFor } from './widgets/widgetVisibility';
import EmptyState from './EmptyState';

export { SIZE_COLS, tierFor };

export type WidgetSize = 'compact' | 'standard' | 'expanded';

/** Per-widget settings bag passed to `render` — reads/writes the entry's
 *  `settings` through the grid's own layout-change path, so a widget's
 *  local prefs (e.g. Budget's Hours/Dollars toggle) persist with the
 *  layout without the widget touching `onLayoutChange` itself. */
export interface WidgetSlot {
  settings: Record<string, unknown>;
  setSettings: (patch: Record<string, unknown>) => void;
}

export interface WidgetDef<Ctx = any> {
  id: string;
  title: string;
  requires?: string[];
  /** Any-of gate, AND-ed with `requires` — for permission families with no
   *  implication map (e.g. forecasting.view_own/_team/_all are exact-match
   *  keys in hasPermission, not tiers), so a single `requires` key would
   *  wrongly hide the widget from a holder of a sibling key. */
  requiresAny?: string[];
  description?: string;
  defaultSize?: WidgetSize;
  /** Can't be hidden — no ✕ in customize mode, listed under "Always on" in
   *  the catalog (not addable/removable), and treated as visible even if a
   *  stored layout entry marks it hidden (legacy data, a since-changed def). */
  required?: boolean;
  /** Doc flag only: this widget accepts user input (vs. read-only display). */
  inputSurface?: boolean;
  /** Render the widget body. `cols` is the column span it is rendered at
   *  (0-12). A widget body shows the SAME superset content at every
   *  width — every field, list, stat, and chart the widget has — and only
   *  reflows its layout by `cols` (e.g. a Fields grid's column count, or a
   *  Stepper's own measured-width flip). There is no size "tier" param:
   *  T3a removed the compact/standard/expanded content branches — see
   *  `WidgetSize`/`tierFor`, which still drive catalog-preview and
   *  layout-sizing only, never body content. */
  render: (ctx: Ctx, cols: number, slot: WidgetSlot) => React.ReactNode;
  /** Optional category so the catalog drawer groups widgets sensibly. */
  category?: 'overview' | 'financial' | 'people' | 'schedule' | 'health';
  /** Tiers this widget renders distinct content for. Undefined ⇒ all
   *  three. When `cols` is absent the width range is derived from these
   *  (compact 3 / standard 6 / expanded 12 — min = smallest, max = largest),
   *  and a width whose tier is unsupported renders the nearest supported. */
  sizes?: WidgetSize[];
  /** Width range in grid columns (of 12). Overrides the range derived
   *  from `sizes`. */
  cols?: { min: number; max: number };
  /** @deprecated Ignored — height is measured from the rendered content. */
  rows?: Partial<Record<WidgetSize, number>>;
  /** Set only for a user-built widget (custom-widget.tsx) — drives the
   *  catalog's "Your widgets" / "Firm widgets" grouping. Undefined for
   *  every stock (built-in) widget. */
  custom?: { ownerId: string; shared: boolean };
}

export interface WidgetLayoutEntry {
  id: string;
  /** Legacy size tier. Still written alongside `w` (as tierFor(w)) so
   *  older clients can read the layout; `w` wins when present. */
  size?: WidgetSize;
  hidden?: boolean;
  /** Grid cell position (12-col grid). Unset ⇒ auto-placed first-fit,
   *  then persisted after the user's first drag. */
  x?: number;
  y?: number;
  /** Width in grid columns (1–12). Unset ⇒ derived from `size`. */
  w?: number;
  /** Per-widget settings bag (persisted with the layout). */
  settings?: Record<string, unknown>;
  // Legacy compat — older layouts used cols/rows; ignored, kept so the
  // JSON parses without throwing.
  cols?: number;
  rows?: number;
}

export interface WidgetGridProps<Ctx> {
  widgets: WidgetDef<Ctx>[];
  layout: WidgetLayoutEntry[];
  userPermissions?: string[];
  ctx: Ctx;
  onLayoutChange?: (next: WidgetLayoutEntry[]) => void;
  defaultLayout?: WidgetLayoutEntry[];
  customizing: boolean;
  /** Optional slot rendered above the grid — used for the Saved Views bar. */
  viewsBar?: React.ReactNode;
  /** Current viewer's team-member id — splits the catalog's custom-widget
   *  section into "Your widgets" vs "Firm widgets" (T4). */
  currentUserId?: string;
  /** Opens the widget builder for a brand-new widget — wires the catalog's
   *  "+ New widget" button (T5-UI). Omitted ⇒ the button is not rendered. */
  onNewWidget?: () => void;
  /** Builder R2 — catalog row "…" menu for a user-built widget (Your/Firm
   *  widgets). `id` is the bare `CustomWidget.id` (the `custom:` prefix
   *  already stripped). Omitted ⇒ no menu (stock-only catalogs keep
   *  today's behaviour). */
  onEditCustomWidget?: (id: string) => void;
  onDuplicateCustomWidget?: (id: string) => void;
  onRenameCustomWidget?: (id: string, name: string) => void;
  onDeleteCustomWidget?: (id: string) => void;
  /** Same manage gate `custom-widget.tsx`'s header menu uses (mirrors the
   *  server exactly) — governs whether the catalog row gets a "…" menu at
   *  all. Omitted ⇒ no custom-widget catalog menu renders. */
  canManageCustomWidget?: (ownerId: string, shared: boolean) => boolean;
}

/** Inert stand-in `WidgetSlot` for the catalog's read-only preview cards —
 *  a preview never persists a settings change. */
const PREVIEW_SLOT: WidgetSlot = { settings: {}, setSettings: () => {} };

const SIZE_ORDER: WidgetSize[] = ['compact', 'standard', 'expanded'];
// Grid cell units feeding react-grid-layout's numeric props + this file's own
// overlap/placement math (not CSS) — stay raw numbers, never tokens/rem.
const GRID_COLS = 12;
/** Row unit and gutter as fractions of the live root font size (rem). */
const ROW_REM = 0.5;
const GAP_REM = 1;
/** Height assumed before a card has been measured (rem). */
const UNMEASURED_REM = 10;
/** Layout id of the "+ Add widget" ghost card (never persisted). */
const ADD_ID = '__wg_add_widget__';

const CATEGORY_LABEL: Record<NonNullable<WidgetDef['category']>, string> = {
  overview: 'Overview',
  financial: 'Financial',
  people: 'People',
  schedule: 'Schedule',
  health: 'Health',
};

const clampInt = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, Math.round(n)));

function readRootPx(): number {
  if (typeof document === 'undefined') return ROOT_PX;
  const v = parseFloat(getComputedStyle(document.documentElement).fontSize);
  return Number.isFinite(v) && v > 0 ? v : ROOT_PX;
}

/** The live root font size in px — re-read on window resize, on any
 *  change to the root element's inline style (the interface-scale
 *  controller writes it there), and whenever the container width moves. */
function useRootPx(containerWidth: number): number {
  const [px, setPx] = useState(readRootPx);
  const update = useCallback(() => {
    const next = readRootPx();
    setPx((prev) => (Math.abs(prev - next) < 0.01 ? prev : next));
  }, []);
  useEffect(() => {
    window.addEventListener('resize', update);
    const mo = new MutationObserver(update);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'class'] });
    return () => {
      window.removeEventListener('resize', update);
      mo.disconnect();
    };
  }, [update]);
  useEffect(() => {
    update();
  }, [containerWidth, update]);
  return px;
}

/** Content heights (px) per grid item, measured by ONE ResizeObserver on
 *  each item's intrinsic-height inner wrapper (never the RGL item div,
 *  which RGL sizes — measuring that would feed back into itself). Updates
 *  are batched into a single state map and only committed when a value
 *  actually changed, so a stable board never re-renders from measurement. */
function useMeasuredHeights() {
  const [heights, setHeights] = useState<Record<string, number>>({});
  const els = useRef(new Map<string, HTMLElement>());
  const refCbs = useRef(new Map<string, (el: HTMLElement | null) => void>());
  const ro = useRef<ResizeObserver | null>(null);

  const measure = (el: HTMLElement): number => {
    // The measured wrapper sits inside the card frame; add the frame's
    // own border so the cell fits the whole card.
    const frameEl = el.parentElement;
    const frame = frameEl ? frameEl.offsetHeight - frameEl.clientHeight : 0;
    return Math.ceil(el.getBoundingClientRect().height + Math.max(0, frame));
  };

  const observer = useCallback((): ResizeObserver | null => {
    if (!ro.current && typeof ResizeObserver !== 'undefined') {
      ro.current = new ResizeObserver((entries) => {
        const updates: Record<string, number> = {};
        for (const en of entries) {
          const el = en.target as HTMLElement;
          const id = el.dataset.measureId;
          if (id) updates[id] = measure(el);
        }
        setHeights((prev) => {
          const changed = Object.keys(updates).some((id) => prev[id] !== updates[id]);
          return changed ? { ...prev, ...updates } : prev;
        });
      });
    }
    return ro.current;
  }, []);

  const refFor = useCallback(
    (id: string) => {
      let cb = refCbs.current.get(id);
      if (!cb) {
        cb = (el: HTMLElement | null) => {
          const prev = els.current.get(id);
          if (prev && prev !== el) observer()?.unobserve(prev);
          if (el) {
            els.current.set(id, el);
            observer()?.observe(el);
          } else {
            els.current.delete(id);
          }
        };
        refCbs.current.set(id, cb);
      }
      return cb;
    },
    [observer]
  );

  useEffect(() => () => ro.current?.disconnect(), []);
  return { heights, refFor };
}

export function WidgetGrid<Ctx>({
  widgets,
  layout,
  userPermissions,
  ctx,
  onLayoutChange,
  defaultLayout,
  customizing,
  viewsBar,
  currentUserId,
  onNewWidget,
  onEditCustomWidget,
  onDuplicateCustomWidget,
  onRenameCustomWidget,
  onDeleteCustomWidget,
  canManageCustomWidget,
}: WidgetGridProps<Ctx>) {
  const [showCatalog, setShowCatalog] = useState(false);
  const [renamingCustomId, setRenamingCustomId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const { width, containerRef, mounted } = useContainerWidth({ initialWidth: 1280 });
  const rootPx = useRootPx(width);
  const { heights, refFor } = useMeasuredHeights();

  // Grid metrics in px for react-grid-layout, derived from rem.
  const rowPx = rootPx * ROW_REM;
  const gapPx = rootPx * GAP_REM;
  const collapsed = width < DASHBOARD_SINGLE_COLUMN_REM * rootPx;
  const editable = customizing && !collapsed;
  const hFor = (px: number | undefined) =>
    Math.max(1, Math.ceil(((px ?? rootPx * UNMEASURED_REM) + gapPx) / (rowPx + gapPx)));

  const allowedWidgets = widgets.filter(
    (w) =>
      (!w.requires || w.requires.every((k) => hasPermission(userPermissions, k))) &&
      (!w.requiresAny || hasAnyPermission(userPermissions, w.requiresAny))
  );
  const allowedIds = new Set(allowedWidgets.map((w) => w.id));
  const defOf = (id: string) => widgets.find((x) => x.id === id);

  /** Width range (cols) — explicit `cols`, else derived from `sizes`. */
  const colRange = (id: string): { min: number; max: number } => {
    const w = defOf(id);
    if (w?.cols) {
      const min = clampInt(w.cols.min, 1, GRID_COLS);
      return { min, max: clampInt(w.cols.max, min, GRID_COLS) };
    }
    if (w?.sizes && w.sizes.length > 0) {
      const spans = w.sizes.map((s) => SIZE_COLS[s]);
      return { min: Math.min(...spans), max: Math.max(...spans) };
    }
    return { min: 3, max: GRID_COLS };
  };

  /** Stored width in cols: `w`, else the legacy size tier, else the even
   *  older cols/rows shape; clamped to the widget's range. */
  const entryCols = (e: WidgetLayoutEntry): number => {
    let w = e.w;
    if (typeof w !== 'number') {
      if (e.size && SIZE_ORDER.includes(e.size)) w = SIZE_COLS[e.size];
      else {
        const legacy = e.cols ?? 2;
        w = SIZE_COLS[legacy >= 4 ? 'expanded' : legacy >= 2 ? 'standard' : 'compact'];
      }
    }
    const { min, max } = colRange(e.id);
    return clampInt(w, min, max);
  };

  const effectiveLayout: WidgetLayoutEntry[] = (
    layout && layout.length > 0 ? layout : defaultLayout || []
  ).filter((e) => allowedIds.has(e.id));

  const visibleEntries = effectiveLayout.filter((e) => {
    if (!e.hidden) return true;
    // A required widget stays visible even if a stored layout entry has it
    // hidden (legacy data, or a def that only became required later).
    return !!defOf(e.id)?.required;
  });

  // Cell footprints + first-fit auto-placement for entries with no stored
  // position (fresh installs, newly added widgets, defaults), then vertical
  // compaction — the same compaction RGL applies, run here so the ghost
  // card can be pinned directly below the last widget.
  const widgetItems: LayoutItem[] = (() => {
    type Cell = { x: number; y: number; w: number; h: number };
    const placed: Cell[] = [];
    const overlaps = (a: Cell, b: Cell) =>
      a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
    const out: LayoutItem[] = [];
    for (const e of visibleEntries) {
      const w = entryCols(e);
      const h = hFor(heights[e.id]);
      const { min, max } = colRange(e.id);
      let cell: Cell | null =
        e.x != null && e.y != null ? { x: Math.min(e.x, GRID_COLS - w), y: e.y, w, h } : null;
      if (!cell) {
        for (let y = 0; cell === null && y < 500; y++) {
          for (let x = 0; x <= GRID_COLS - w; x++) {
            const cand = { x, y, w, h };
            if (!placed.some((p) => overlaps(cand, p))) {
              cell = cand;
              break;
            }
          }
        }
        if (!cell) cell = { x: 0, y: 500, w, h };
      }
      placed.push(cell);
      out.push({
        i: e.id,
        ...cell,
        minW: min,
        maxW: max,
        // Height is content-derived, never user-resizable.
        minH: h,
        maxH: h,
        // Per-item flag overrides the grid-level one — gate it on Customize.
        isResizable: editable && min < max,
      });
    }
    return verticalCompactor.compact(out, GRID_COLS) as LayoutItem[];
  })();

  // Collapsed: one column in reading order (y, then x). Stored layout untouched.
  const colPxAtThreshold = (DASHBOARD_SINGLE_COLUMN_REM * rootPx) / GRID_COLS;
  const collapsedCols = Math.max(1, Math.min(GRID_COLS, Math.round(width / colPxAtThreshold)));
  const displayItems: LayoutItem[] = collapsed
    ? (() => {
        let y = 0;
        return [...widgetItems]
          .sort((a, b) => a.y - b.y || a.x - b.x)
          .map((it) => {
            const o: LayoutItem = {
              i: it.i,
              x: 0,
              y,
              w: 1,
              h: it.h,
              minH: it.h,
              maxH: it.h,
              isDraggable: false,
              isResizable: false,
            };
            y += it.h;
            return o;
          });
      })()
    : widgetItems;

  const gridItems: Layout = customizing
    ? [
        ...displayItems,
        {
          i: ADD_ID,
          x: 0,
          y: bottom(displayItems),
          w: collapsed ? 1 : GRID_COLS,
          h: hFor(heights[ADD_ID]),
          static: true,
          isDraggable: false,
          isResizable: false,
        },
      ]
    : displayItems;

  /** Cols a widget is rendered at: its stored width, or in the collapsed
   *  column the smaller of that and what the column can hold. */
  const renderColsFor = (id: string): number => {
    const it = widgetItems.find((x) => x.i === id);
    const w = it ? it.w : 6;
    return collapsed ? Math.min(w, collapsedCols) : w;
  };

  const catalogWidgets = allowedWidgets.filter((w) => {
    if (w.required) return false; // never addable/removable — always on
    const entry = effectiveLayout.find((e) => e.id === w.id);
    return !entry || entry.hidden;
  });

  function updateLayout(updater: (prev: WidgetLayoutEntry[]) => WidgetLayoutEntry[]) {
    onLayoutChange?.(updater(effectiveLayout));
  }

  /** After a user drag/resize: write RGL's final cell positions + widths
   *  back into the entries (height is derived, never persisted). Skips the
   *  write when nothing moved. */
  function persistIfMoved(l: Layout) {
    if (collapsed) return;
    const moved = l.some((it) => {
      if (it.i === ADD_ID) return false;
      const cur = widgetItems.find((x) => x.i === it.i);
      const entry = visibleEntries.find((e) => e.id === it.i);
      return (
        !cur ||
        cur.x !== it.x ||
        cur.y !== it.y ||
        cur.w !== it.w ||
        entry?.x == null ||
        entry?.y == null ||
        entry?.w !== it.w
      );
    });
    if (!moved) return;
    updateLayout((prev) =>
      prev.map((e) => {
        const item = l.find((it) => it.i === e.id);
        return item ? { ...e, x: item.x, y: item.y, w: item.w, size: tierFor(item.w) } : e;
      })
    );
  }

  /** A drag/resize can settle a widget above or below the scroll
   *  container's current viewport (its new row may sit outside where the
   *  user was scrolled to before the move) — bring it back into view so
   *  its header/resize grip stay reachable for the next interaction.
   *  `scrollIntoView` walks up to the nearest scrollable ancestor
   *  (`#project-dashboard-grid`, owned by the host page) on its own. */
  function scrollItemIntoView(id: string) {
    // RGL settles a drag/resize with a CSS transform transition — wait for
    // it to finish (its default duration) before reading the item's final
    // rect, or scrollIntoView targets the still-animating position.
    setTimeout(() => {
      document
        .querySelector(`[data-widget-id="${id}"]`)
        ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, 260);
  }

  function setWidgetCols(id: string, cols: number) {
    const { min, max } = colRange(id);
    const w = clampInt(cols, min, max);
    updateLayout((prev) => prev.map((e) => (e.id === id ? { ...e, w, size: tierFor(w) } : e)));
  }

  function hideWidget(id: string) {
    updateLayout((prev) => prev.map((e) => (e.id === id ? { ...e, hidden: true } : e)));
  }

  function setWidgetSettings(id: string, patch: Record<string, unknown>) {
    updateLayout((prev) =>
      prev.map((e) => (e.id === id ? { ...e, settings: { ...e.settings, ...patch } } : e))
    );
  }

  function showWidget(widget: WidgetDef<Ctx>) {
    const size = widget.defaultSize ?? 'standard';
    updateLayout((prev) => {
      const existing = prev.find((e) => e.id === widget.id);
      if (existing)
        return prev.map((e) =>
          e.id === widget.id ? { ...e, hidden: false, x: undefined, y: undefined } : e
        );
      return [...prev, { id: widget.id, size, w: SIZE_COLS[size], hidden: false }];
    });
    setShowCatalog(false);
  }

  // Keyboard resize on the east grip (the grip is rendered by RGL without
  // item context, so it reads the widget id from its item element).
  const keyResizeRef = useRef<(id: string, delta: number) => void>(() => {});
  keyResizeRef.current = (id, delta) => {
    const it = widgetItems.find((x) => x.i === id);
    if (it) setWidgetCols(id, it.w + delta);
  };
  const onGripKey = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    const id = (e.currentTarget.parentElement as HTMLElement | null)?.dataset.widgetId;
    if (!id) return;
    e.preventDefault();
    keyResizeRef.current(id, e.key === 'ArrowRight' ? 1 : -1);
  }, []);
  const renderGrip = useCallback(
    (axis: ResizeHandleAxis, ref: React.Ref<HTMLElement>) => (
      <span
        ref={ref as React.Ref<HTMLSpanElement>}
        className={`react-resizable-handle react-resizable-handle-${axis} wg-resize-handle`}
        role="button"
        tabIndex={0}
        aria-label="Resize widget width — drag, or press Left / Right arrow"
        title="Drag to resize width"
        onKeyDown={onGripKey}
      />
    ),
    [onGripKey]
  );

  // Force height back to the measured value while resizing (width only).
  const lockHeight = (
    _l: Layout,
    _old: LayoutItem | null,
    item: LayoutItem | null,
    placeholder: LayoutItem | null
  ) => {
    if (!item) return;
    const measured = widgetItems.find((x) => x.i === item.i)?.h;
    if (measured == null) return;
    item.h = measured;
    if (placeholder) placeholder.h = measured;
  };

  const gridConfig = useMemo(
    () => ({
      cols: collapsed ? 1 : GRID_COLS,
      rowHeight: rowPx,
      margin: [gapPx, gapPx] as const,
      containerPadding: [0, 0] as const,
    }),
    [collapsed, rowPx, gapPx]
  );
  const dragConfig = useMemo(
    () => ({ enabled: editable, handle: '.wg-drag-handle', cancel: 'button' }),
    [editable]
  );
  const resizeConfig = useMemo(
    () => ({ enabled: editable, handles: ['e'] as const, handleComponent: renderGrip }),
    [editable, renderGrip]
  );

  // Stock (built-in) catalog entries, grouped by category — custom
  // (user-built) widgets get their own "Your widgets" / "Firm widgets"
  // sections below instead (T4).
  const stockCatalogWidgets = catalogWidgets.filter((w) => !w.custom);
  const yourCustomWidgets = catalogWidgets.filter(
    (w) => w.custom && w.custom.ownerId === currentUserId
  );
  const firmCustomWidgets = catalogWidgets.filter(
    (w) => w.custom && w.custom.ownerId !== currentUserId
  );
  const catalogByCategory = stockCatalogWidgets.reduce<Record<string, WidgetDef<Ctx>[]>>(
    (acc, w) => {
      const cat = w.category || 'overview';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(w);
      return acc;
    },
    {}
  );

  /** One catalog card: title + description + a non-interactive live preview
   *  of the real widget body at 3 columns (T4) — `inert` + pointer-events
   *  none so the preview can never be focused/clicked/scrolled, only the
   *  card's own button (or, for "Always on", nothing) is. */
  const catalogCard = (w: WidgetDef<Ctx>, clickable: boolean) => {
    const customId = w.custom ? w.id.replace(/^custom:/, '') : null;
    const canManageThis = !!(
      customId &&
      w.custom &&
      canManageCustomWidget?.(w.custom.ownerId, w.custom.shared)
    );
    const isRenaming = customId != null && renamingCustomId === customId;
    const commitRename = () => {
      const next = renameDraft.trim();
      setRenamingCustomId(null);
      if (customId && next && next !== w.title) onRenameCustomWidget?.(customId, next);
    };
    return (
      <div
        key={w.id}
        onClick={clickable && !isRenaming ? () => showWidget(w) : undefined}
        role={clickable && !isRenaming ? 'button' : undefined}
        tabIndex={clickable && !isRenaming ? 0 : undefined}
        onKeyDown={
          clickable && !isRenaming
            ? (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  showWidget(w);
                }
              }
            : undefined
        }
        style={{
          textAlign: 'left',
          padding: `var(--space-3) ${rem(14)}`,
          border: clickable ? '1px solid var(--bd)' : '1px dashed var(--bd)',
          borderRadius: rem(8),
          background: 'transparent',
          cursor: clickable && !isRenaming ? 'pointer' : 'default',
          display: 'flex',
          flexDirection: 'column',
          gap: rem(6),
        }}
      >
        {customId && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ display: 'flex', alignItems: 'center', gap: rem(6) }}
          >
            {isRenaming ? (
              <Input
                autoFocus
                value={renameDraft}
                onChange={(e) => setRenameDraft(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitRename();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    setRenamingCustomId(null);
                  }
                }}
                style={{ flex: 1, height: rem(28) }}
              />
            ) : (
              <span
                style={{
                  flex: 1,
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  color: 'var(--tx)',
                }}
              >
                {w.title}
              </span>
            )}
            {canManageThis && (
              <ContextMenu>
                <ContextMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label={`More actions for ${w.title}`}
                    title="More actions"
                    className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-transparent text-muted transition-colors hover:bg-hover hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <Icon icon={MoreHorizontal} size="sm" />
                  </button>
                </ContextMenuTrigger>
                <ContextMenuContent
                  align="end"
                  sideOffset={4}
                  className="min-w-[10rem] rounded-md border border-border bg-surface p-1 shadow-md"
                >
                  <ContextMenuItem onSelect={() => onEditCustomWidget?.(customId)}>
                    Edit
                  </ContextMenuItem>
                  <ContextMenuItem onSelect={() => onDuplicateCustomWidget?.(customId)}>
                    Duplicate
                  </ContextMenuItem>
                  <ContextMenuItem
                    onSelect={() => {
                      setRenameDraft(w.title);
                      setRenamingCustomId(customId);
                    }}
                  >
                    Rename
                  </ContextMenuItem>
                  <ContextMenuItem
                    onSelect={() => onDeleteCustomWidget?.(customId)}
                    className="text-danger"
                  >
                    Delete
                  </ContextMenuItem>
                </ContextMenuContent>
              </ContextMenu>
            )}
          </div>
        )}
        {w.description && (
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)', lineHeight: 1.4 }}>
            {w.description}
          </span>
        )}
        <div
          aria-hidden="true"
          {...({ inert: '' } as Record<string, unknown>)}
          style={{
            pointerEvents: 'none',
            padding: 'var(--space-2)',
            border: '1px solid var(--bd)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--bg)',
            overflow: 'hidden',
            maxHeight: rem(160),
          }}
          className="wg-preview"
        >
          {w.render(ctx, 3, PREVIEW_SLOT)}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{ position: 'relative' }}
      className={editable ? 'wg-grid wg-grid--editing' : 'wg-grid'}
      ref={containerRef as React.RefObject<HTMLDivElement>}
    >
      {viewsBar}
      {mounted && (
        <GridLayout
          width={width}
          layout={gridItems}
          gridConfig={gridConfig}
          dragConfig={dragConfig}
          resizeConfig={resizeConfig}
          onResize={lockHeight}
          onDragStop={(l: Layout, _o, item) => {
            persistIfMoved(l);
            if (item) scrollItemIntoView(item.i);
          }}
          onResizeStop={(l: Layout, _o, item) => {
            lockHeight(l, null, item, null);
            persistIfMoved(l);
            if (item) scrollItemIntoView(item.i);
          }}
        >
          {displayItems.map((item) => {
            const widget = defOf(item.i);
            if (!widget) return <div key={item.i} />;
            const cols = renderColsFor(item.i);
            return (
              <div key={item.i} data-widget-id={item.i}>
                {/* Card frame fills the cell; the inner wrapper keeps its
                    intrinsic height and is what gets measured. */}
                <div
                  style={{
                    height: '100%',
                    background: 'var(--sf)',
                    border: '1px solid var(--bd)',
                    borderRadius: 'var(--radius-lg)',
                    overflow: 'hidden',
                    position: 'relative',
                    boxShadow: customizing ? '0 0 0 1px rgba(var(--acr), 0.08)' : 'none',
                  }}
                >
                  {/* Customize overlay: hide ✕ only (the WidgetFrame header is
                    the card's one header AND its drag grip, `.wg-drag-handle`). */}
                  {customizing && !widget.required && (
                    <div className="wg-hide-overlay">
                      <button
                        type="button"
                        className="wg-hide-btn"
                        onClick={() => hideWidget(item.i)}
                        title="Hide widget"
                        aria-label={`Hide ${widget.title}`}
                      >
                        &#10005;
                      </button>
                    </div>
                  )}
                  <div ref={refFor(item.i)} data-measure-id={item.i}>
                    {/* Body — the widget's own WidgetFrame (header + content);
                        grows with its content, the card never scrolls. */}
                    <div
                      data-widget-body
                      style={{
                        overflow: 'visible',
                        padding: `${rem(14)} var(--space-4) var(--space-4)`,
                      }}
                    >
                      {widget.render(ctx, cols, {
                        settings: effectiveLayout.find((e) => e.id === item.i)?.settings ?? {},
                        setSettings: (patch) => setWidgetSettings(item.i, patch),
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {customizing && (
            <div key={ADD_ID} data-wg-add>
              <div
                ref={refFor(ADD_ID)}
                data-measure-id={ADD_ID}
                style={{
                  border: '1px dashed var(--bd)',
                  borderRadius: 'var(--radius-lg)',
                  padding: 'var(--space-4)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                }}
              >
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCatalog(true)}
                  // Builder R2 fix: "+ New widget" lives INSIDE this same
                  // Sheet (below) — a user who already sees every stock
                  // widget (the common case for a full-permission role)
                  // could never reach it while this stayed gated on
                  // catalogWidgets.length alone. Only truly dead-end (no
                  // catalog AND no builder) disables it now.
                  disabled={catalogWidgets.length === 0 && !onNewWidget}
                >
                  + Add widget{catalogWidgets.length > 0 ? ` (${catalogWidgets.length})` : ''}
                </Button>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
                  {catalogWidgets.length > 0
                    ? 'Pick from the widget catalog.'
                    : onNewWidget
                      ? 'All stock widgets are already on your dashboard — build your own.'
                      : 'All widgets already on your dashboard.'}
                </span>
              </div>
            </div>
          )}
        </GridLayout>
      )}

      {/* Customize footer */}
      {customizing && (
        <div
          style={{
            marginTop: 'var(--space-4)',
            display: 'flex',
            gap: 'var(--space-2)',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          {defaultLayout && defaultLayout.length > 0 && (
            <button
              onClick={() => updateLayout(() => defaultLayout.slice())}
              style={{
                padding: `var(--space-2) ${rem(14)}`,
                border: '1px solid var(--bd)',
                borderRadius: 'var(--radius-md)',
                background: 'transparent',
                color: 'var(--tm)',
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
              }}
            >
              Reset to default
            </button>
          )}
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--tm)',
              flexBasis: '100%',
              textAlign: 'center',
            }}
          >
            {collapsed
              ? 'Widen the window to rearrange or resize widgets.'
              : 'Drag a header to move a widget; drag its right edge (or use the arrow keys) to change its width. Height always fits the content.'}
          </span>
        </div>
      )}

      {/* Catalog panel — kit Sheet: Esc, focus trap, focus return, body
          portal, all for free (replaces the former hand-rolled overlay). */}
      <Sheet open={showCatalog} onOpenChange={setShowCatalog}>
        <SheetContent side="right">
          <SheetHeader>
            <SheetTitle>Add widget</SheetTitle>
            <SheetDescription>{catalogWidgets.length} available</SheetDescription>
          </SheetHeader>
          <div
            style={{
              marginTop: 'var(--space-4)',
              display: 'flex',
              flexDirection: 'column',
              gap: rem(18),
            }}
          >
            {(['overview', 'financial', 'people', 'schedule', 'health'] as const).map((cat) => {
              const items = catalogByCategory[cat];
              if (!items || items.length === 0) return null;
              return (
                <div
                  key={cat}
                  style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}
                >
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 700,
                      color: 'var(--tm)',
                      // eslint-disable-next-line sf/no-raw-px -- 1px, below the >4 conversion threshold
                      letterSpacing: 1,
                      textTransform: 'uppercase',
                      padding: '0 var(--space-1)',
                    }}
                  >
                    {CATEGORY_LABEL[cat]}
                  </div>
                  {items.map((w) => catalogCard(w, true))}
                </div>
              );
            })}
            {onNewWidget && hasPermission(userPermissions, 'dashboards.create') && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowCatalog(false);
                  onNewWidget();
                }}
              >
                + New widget
              </Button>
            )}
            {yourCustomWidgets.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <div
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 700,
                    color: 'var(--tm)',
                    // eslint-disable-next-line sf/no-raw-px -- 1px, below the >4 conversion threshold
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                    padding: '0 var(--space-1)',
                  }}
                >
                  Your widgets
                </div>
                {yourCustomWidgets.map((w) => catalogCard(w, true))}
              </div>
            )}
            {firmCustomWidgets.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <div
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 700,
                    color: 'var(--tm)',
                    // eslint-disable-next-line sf/no-raw-px -- 1px, below the >4 conversion threshold
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                    padding: '0 var(--space-1)',
                  }}
                >
                  Firm widgets
                </div>
                {firmCustomWidgets.map((w) => catalogCard(w, true))}
              </div>
            )}
            {catalogWidgets.length === 0 && (
              <EmptyState title="Everything is on your board." compact />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
